// Set Size & Colour - turn products into variants (one product + size + colour,
// each with its own SKU and barcodes, and an optional price / re-order level).
// Handover section 6.4.
//
//   1. pick the products: one or several, or every product in a sub-category
//      (?product=<guid> opens with that product picked)
//   2. pick the size set, its sizes and the colours
//   3. the grid previews every product x size x colour; rows that already exist
//      keep their SKU and barcodes, new ones get barcodes typed in or generated
//   4. Save sends the whole grid in one SizeColour/SaveVariants call
import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { fetchCategory, fetchProduct } from "../../redux/userSetting";
import {
  deleteVariants,
  getColours,
  getNextBarcodes,
  getSizeSets,
  getSubCategories,
  getVariants,
  saveVariants,
} from "../../services/SizeColourApiServices";
import {
  AdminScreen,
  AxField,
  AxPlusIcon,
  AxRefreshIcon,
  AxSectionHead,
  AxSelectField,
  AxStatusKeys,
  AxTodoNote,
} from "../../components/adminsitration/ui";

const MySwal = withReactContent(Swal);
const arr = (x) => (Array.isArray(x) ? x : []);
const errMsg = (e, fallback) => e?.response?.data?.message || e?.response?.data?.Message || fallback;
const keyOf = (p, s, c) => `${String(p).toLowerCase()}|${String(s).toLowerCase()}|${String(c).toLowerCase()}`;
const bcType = (v) => (/^\d{13}$/.test(v) ? "EAN-13" : /^\d{8}$/.test(v) ? "EAN-8" : "Code128");
const numOrNull = (v) => (v === "" || v === null || v === undefined || Number.isNaN(Number(v)) ? null : Number(v));

const SetSizeColour = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { branch } = useSelector((state) => state.branchlist);
  const { userId } = useSelector((state) => state.userauth);
  const { productList, categoryList } = useSelector((state) => state.usersetting);
  const enabled = useSelector((state) => state.settings?.branchsettings?.enableSizeColour);

  const [sizeSets, setSizeSets] = useState([]);
  const [colours, setColours] = useState([]);
  const [picked, setPicked] = useState([]); // product guids
  const [find, setFind] = useState("");
  const [cat, setCat] = useState("");
  const [subCats, setSubCats] = useState([]);
  const [sub, setSub] = useState("");
  const [sizeSetID, setSizeSetID] = useState("");
  const [sizeOn, setSizeOn] = useState({}); // size guid -> picked
  const [colourOn, setColourOn] = useState({}); // colour guid -> picked
  const [existing, setExisting] = useState({}); // key -> variant from the server
  const [edits, setEdits] = useState({}); // key -> { salesRate, reorderLevel, isActive, barcodes }
  const [opts, setOpts] = useState({ onlyAddNew: false, removeMissing: false, autoBarcode: true });
  const [del, setDel] = useState({}); // variantID -> picked for delete
  const [busy, setBusy] = useState(false);
  const updatedUser = `{${userId?.id}}`;

  /* ------------------------------------------------------------ masters */
  useEffect(() => {
    if (branch?.guid) {
      dispatch(fetchProduct(branch));
      dispatch(fetchCategory(branch.guid));
    }
    getSizeSets().then((r) => setSizeSets(arr(r))).catch(() => setSizeSets([]));
    getColours().then((r) => setColours(arr(r))).catch(() => setColours([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch?.guid]);
  useEffect(() => {
    if (!cat) return setSubCats([]);
    getSubCategories(cat).then((r) => setSubCats(arr(r))).catch(() => setSubCats([]));
  }, [cat]);

  const products = arr(productList);
  const prodOf = useCallback((g) => products.find((p) => p.guid === g), [products]);
  const set = sizeSets.find((s) => s.guid === sizeSetID);
  const sizes = useMemo(
    () => arr(set?.sizes).slice().sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0)),
    [set]
  );

  // ?product=<guid> from the product page
  useEffect(() => {
    const g = new URLSearchParams(location.search).get("product");
    if (g && !picked.length && prodOf(g)) addProducts([g]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, products.length]);

  /* ------------------------------------------------------------ products */
  const loadExisting = useCallback(async (ids) => {
    if (!ids.length) return setExisting({});
    try {
      const rows = arr(await getVariants(ids));
      const m = {};
      rows.forEach((v) => (m[keyOf(v.productID, v.sizeID, v.colourID)] = v));
      setExisting(m);
      // the sizes / colours already in use start picked
      setColourOn((c) => ({ ...Object.fromEntries(rows.map((v) => [v.colourID, true])), ...c }));
    } catch (e) {
      toast.error(errMsg(e, "Could not load the existing variants"));
    }
  }, []);
  const addProducts = (ids) => {
    const next = [...picked, ...ids.filter((g) => !picked.includes(g))];
    setPicked(next);
    if (!sizeSetID) {
      const first = next.map(prodOf).find((p) => p?.sizeSetID && p.sizeSetID !== "00000000-0000-0000-0000-000000000000");
      if (first) pickSet(first.sizeSetID);
    }
    loadExisting(next);
  };
  const removeProduct = (g) => {
    const next = picked.filter((x) => x !== g);
    setPicked(next);
    loadExisting(next);
  };
  const addSub = () => {
    const ids = products.filter((p) => p.subCategoryID === sub).map((p) => p.guid);
    if (!ids.length) return toast.error("No product is in that sub-category");
    const s = subCats.find((x) => x.guid === sub);
    if (s?.sizeSetID && !sizeSetID) pickSet(s.sizeSetID);
    addProducts(ids);
  };
  const matches = useMemo(() => {
    const s = find.trim().toLowerCase();
    if (!s) return [];
    return products
      .filter((p) => !picked.includes(p.guid))
      .filter((p) => [p.name, p.code, p.prodCode].some((x) => String(x || "").toLowerCase().includes(s)))
      .slice(0, 8);
  }, [find, products, picked]);

  /* ------------------------------------------------------------ sizes / colours */
  const pickSet = (g) => {
    setSizeSetID(g);
    const s = sizeSets.find((x) => x.guid === g);
    setSizeOn(Object.fromEntries(arr(s?.sizes).map((z) => [z.guid, true])));
  };
  // size sets may arrive after the product picked one
  useEffect(() => {
    if (sizeSetID && !Object.keys(sizeOn).length && set) setSizeOn(Object.fromEntries(arr(set.sizes).map((z) => [z.guid, true])));
  }, [sizeSetID, set, sizeOn]);

  /* ------------------------------------------------------------ the grid */
  const rows = useMemo(() => {
    const out = [];
    picked.forEach((pg) => {
      const p = prodOf(pg);
      if (!p) return;
      sizes
        .filter((z) => sizeOn[z.guid])
        .forEach((z) =>
          colours
            .filter((c) => colourOn[c.guid])
            .forEach((c) => {
              const k = keyOf(pg, z.guid, c.guid);
              const ex = existing[k];
              out.push({ k, p, z, c, ex, skip: !!ex && opts.onlyAddNew });
            })
        );
    });
    return out;
  }, [picked, prodOf, sizes, sizeOn, colours, colourOn, existing, opts.onlyAddNew]);
  const inGrid = new Set(rows.map((r) => r.k));
  const gone = Object.entries(existing)
    .filter(([k, v]) => !inGrid.has(k) && picked.some((g) => String(g).toLowerCase() === String(v.productID).toLowerCase()))
    .map(([k, v]) => ({ k, v }));

  // the row's working values: what was typed, else what the server has
  const valOf = (r) => {
    const e = edits[r.k] || {};
    const ex = r.ex || {};
    return {
      salesRate: "salesRate" in e ? e.salesRate : ex.salesRate ?? "",
      reorderLevel: "reorderLevel" in e ? e.reorderLevel : ex.reorderLevel ?? "",
      isActive: "isActive" in e ? e.isActive : ex.isActive !== false,
      barcodes: e.barcodes || arr(ex.barcodes).map((b) => ({ barcode: b.barcode, barcodeType: b.barcodeType, source: b.source || "Manual" })),
    };
  };
  const edit = (k, patch) => setEdits((m) => ({ ...m, [k]: { ...(m[k] || {}), ...patch } }));
  const setBarcode = (r, i, v) => {
    const b = valOf(r).barcodes.map((x) => ({ ...x }));
    b[i] = { barcode: v, barcodeType: bcType(v.trim()), source: "Manual" };
    edit(r.k, { barcodes: b });
  };
  const addBarcode = (r) => edit(r.k, { barcodes: [...valOf(r).barcodes, { barcode: "", barcodeType: "EAN-13", source: "Manual" }] });
  const dropBarcode = (r, i) => edit(r.k, { barcodes: valOf(r).barcodes.filter((_, j) => j !== i) });

  // fill every row that has no barcode with the server's next EAN-13s
  const generate = async () => {
    const need = rows.filter((r) => !r.skip && !valOf(r).barcodes.some((b) => String(b.barcode).trim()));
    if (!need.length) return toast("Every row already has a barcode");
    try {
      const codes = arr(await getNextBarcodes(need.length));
      setEdits((m) => {
        const n = { ...m };
        need.forEach((r, i) => {
          if (codes[i]) n[r.k] = { ...(n[r.k] || {}), barcodes: [{ barcode: codes[i], barcodeType: "EAN-13", source: "Auto" }] };
        });
        return n;
      });
    } catch (e) {
      toast.error(errMsg(e, "Could not get barcodes"));
    }
  };
  const clearAll = (field) =>
    setEdits((m) => {
      const n = { ...m };
      rows.forEach((r) => (n[r.k] = { ...(n[r.k] || {}), [field]: field === "barcodes" ? [] : "" }));
      return n;
    });

  /* ------------------------------------------------------------ save / delete */
  const dupes = (() => {
    const seen = {};
    const out = new Set();
    rows.forEach((r) =>
      valOf(r).barcodes.forEach((b) => {
        const v = String(b.barcode).trim();
        if (!v) return;
        if (seen[v]) out.add(v);
        seen[v] = true;
      })
    );
    return out;
  })();
  const save = async () => {
    if (!sizeSetID) return toast.error("Pick a size set");
    if (!rows.length) return toast.error("Pick at least one product, size and colour");
    if (dupes.size) return toast.error(`Barcode used twice: ${[...dupes].join(", ")}`);
    if (opts.removeMissing && gone.length) {
      const ok = await MySwal.fire({
        title: `Remove ${gone.length} variants?`,
        text: "Variants not in the grid are deleted, or made inactive when transactions use them.",
        showCancelButton: true,
        confirmButtonText: "Save and remove",
      });
      if (!ok.isConfirmed) return;
    }
    setBusy(true);
    try {
      const variants = rows
        .filter((r) => !r.skip)
        .map((r) => {
          const v = valOf(r);
          return {
            productID: r.p.guid,
            sizeID: r.z.guid,
            colourID: r.c.guid,
            salesRate: numOrNull(v.salesRate),
            reorderLevel: numOrNull(v.reorderLevel),
            isActive: !!v.isActive,
            barcodes: v.barcodes
              .filter((b) => String(b.barcode).trim())
              .map((b) => ({ barcode: String(b.barcode).trim(), barcodeType: b.barcodeType || bcType(b.barcode), source: b.source || "Manual" })),
          };
        });
      const res = arr(await saveVariants({ sizeSetID, ...opts, updatedUser, variants }));
      const made = res.filter((x) => x.isNew).length;
      const skipped = res.filter((x) => x.skipped).length;
      toast.success(`Saved ${res.length - skipped} variants${made ? ` · ${made} new` : ""}${skipped ? ` · ${skipped} skipped` : ""}`);
      setEdits({});
      await loadExisting(picked);
      if (branch?.guid) dispatch(fetchProduct(branch));
    } catch (e) {
      toast.error(errMsg(e, "Could not save the variants"));
    } finally {
      setBusy(false);
    }
  };
  const delIDs = Object.keys(del).filter((g) => del[g]);
  const removeSelected = async () => {
    const ok = await MySwal.fire({
      title: `Delete ${delIDs.length} variants?`,
      text: "Variants used by transactions are made inactive instead.",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });
    if (!ok.isConfirmed) return;
    try {
      await deleteVariants({ variantIDs: delIDs, updatedUser });
      toast.success("Variants deleted");
      setDel({});
      loadExisting(picked);
    } catch (e) {
      toast.error(errMsg(e, "Could not delete the variants"));
    }
  };

  /* ------------------------------------------------------------ render */
  const newRows = rows.filter((r) => !r.ex && !r.skip).length;
  const chip = (on, label, onClick, extra) => (
    <button type="button" className="axsc-chip" aria-pressed={!!on} onClick={onClick}>
      {extra}
      {label}
    </button>
  );

  const editor = (
    <div className="axa-detailform">
      <div className="axa-tabbody">
        {enabled === false && (
          <AxTodoNote>Size &amp; colour is switched off in Settings › General. Variants saved here are not offered on any screen until it is on.</AxTodoNote>
        )}

        <AxSectionHead>1 · Products</AxSectionHead>
        <div className="axsc-pickers">
          <AxField label="Add a product" hint={find && !matches.length ? "No product matches" : ""}>
            <input type="text" placeholder="Name or code" value={find} onChange={(e) => setFind(e.target.value)} />
            {matches.length > 0 && (
              <div className="axsc-chips" style={{ marginTop: 6 }}>
                {matches.map((p) =>
                  chip(false, `${p.code || p.prodCode || ""} ${p.name}`.trim(), () => {
                    addProducts([p.guid]);
                    setFind("");
                  }, <AxPlusIcon />)
                )}
              </div>
            )}
          </AxField>
          <AxSelectField label="Or a whole sub-category: category" value={cat} onChange={(e) => {
              setCat(e.target.value);
              setSub("");
            }}
          >
            <option value="">Pick a category</option>
            {arr(categoryList).map((c) => (
              <option key={c.guid} value={c.guid}>
                {c.name}
              </option>
            ))}
          </AxSelectField>
          <AxField label="Sub-category">
            <span className="axsc-hexrow">
              <select value={sub} onChange={(e) => setSub(e.target.value)} disabled={!cat} aria-label="Sub-category">
                <option value="">{cat ? "Pick a sub-category" : "—"}</option>
                {subCats.map((s) => (
                  <option key={s.guid} value={s.guid}>
                    {s.name}
                  </option>
                ))}
              </select>
              <button type="button" className="axa-btn axa-btn-ghost" disabled={!sub} onClick={addSub}>
                Add all
              </button>
            </span>
          </AxField>
        </div>
        {picked.length > 0 && (
          <div className="axsc-prods" style={{ marginTop: 10 }}>
            <table className="axsc-grid">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Product</th>
                  <th>Size &amp; colour</th>
                  <th>Variants</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {picked.map((g) => {
                  const p = prodOf(g) || {};
                  const n = Object.values(existing).filter((v) => String(v.productID).toLowerCase() === String(g).toLowerCase()).length;
                  return (
                    <tr key={g}>
                      <td className="mono">{p.code || p.prodCode}</td>
                      <td>{p.name}</td>
                      <td>{p.isSizeColourEnabled ? "on" : "off (switched on when saved)"}</td>
                      <td>{n}</td>
                      <td className="c">
                        <button type="button" className="axa-iconbtn" title="Take out" onClick={() => removeProduct(g)}>
                          ×
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <AxSectionHead>2 · Sizes and colours</AxSectionHead>
        <div className="axsc-pickers">
          <AxSelectField label="Size set" required value={sizeSetID} onChange={(e) => pickSet(e.target.value)}>
            <option value="">{sizeSets.length ? "Pick a size set" : "No size sets yet - add them in Size & Colour"}</option>
            {sizeSets.map((s) => (
              <option key={s.guid} value={s.guid}>
                {s.name}
              </option>
            ))}
          </AxSelectField>
        </div>
        {sizes.length > 0 && (
          <AxField label={`Sizes · ${sizes.filter((z) => sizeOn[z.guid]).length} of ${sizes.length}`}>
            <div className="axsc-chips">
              {sizes.map((z) => chip(sizeOn[z.guid], z.code, () => setSizeOn((m) => ({ ...m, [z.guid]: !m[z.guid] })), z.isCore ? <span className="core">core</span> : null))}
            </div>
          </AxField>
        )}
        <AxField label={`Colours · ${colours.filter((c) => colourOn[c.guid]).length} of ${colours.length}`}>
          <div className="axsc-chips">
            {colours.length
              ? colours.map((c) =>
                  chip(colourOn[c.guid], c.name, () => setColourOn((m) => ({ ...m, [c.guid]: !m[c.guid] })), <span className="axsc-swatch" style={{ background: c.hexCode }} />)
                )
              : "No colours yet - add them in Size & Colour"}
          </div>
        </AxField>

        <AxSectionHead>3 · Variants</AxSectionHead>
        <div className="axsc-opts">
          <label className="axst-check">
            <input type="checkbox" checked={opts.onlyAddNew} onChange={(e) => setOpts((o) => ({ ...o, onlyAddNew: e.target.checked }))} />
            Only add new (leave existing variants as they are)
          </label>
          <label className="axst-check">
            <input type="checkbox" checked={opts.removeMissing} onChange={(e) => setOpts((o) => ({ ...o, removeMissing: e.target.checked }))} />
            Remove variants not in the grid
          </label>
          <label className="axst-check">
            <input type="checkbox" checked={opts.autoBarcode} onChange={(e) => setOpts((o) => ({ ...o, autoBarcode: e.target.checked }))} />
            Auto barcode for rows with none
          </label>
        </div>
        <div className="axsc-sum">
          <span>
            <b>{rows.length}</b> in the grid
          </span>
          <span>
            <b>{newRows}</b> new
          </span>
          <span>
            <b>{rows.length - newRows}</b> existing
          </span>
          {opts.removeMissing && gone.length > 0 && (
            <span style={{ color: "var(--danger)" }}>
              <b>{gone.length}</b> to remove
            </span>
          )}
          <span className="sp" style={{ flex: 1 }} />
          <button type="button" className="axa-btn axa-btn-ghost" onClick={generate} disabled={!rows.length}>
            Generate barcodes
          </button>
          <button type="button" className="axa-btn axa-btn-ghost" onClick={() => clearAll("salesRate")} disabled={!rows.length}>
            Clear prices
          </button>
          <button type="button" className="axa-btn axa-btn-ghost" onClick={() => clearAll("reorderLevel")} disabled={!rows.length}>
            Clear re-order
          </button>
          <button
            type="button"
            className="axa-btn axa-btn-ghost"
            disabled={!rows.length}
            onClick={() => {
              clearAll("barcodes");
              setOpts((o) => ({ ...o, autoBarcode: false }));
            }}
          >
            Clear barcodes
          </button>
        </div>
        {rows.length ? (
          <div className="axsc-vwrap">
            <table className="axsc-grid">
              <thead>
                <tr>
                  <th />
                  <th>Product</th>
                  <th>Size</th>
                  <th>Colour</th>
                  <th>SKU</th>
                  <th>Barcodes</th>
                  <th>Price</th>
                  <th>Re-order</th>
                  <th>Active</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const v = valOf(r);
                  const bcs = v.barcodes.length ? v.barcodes : [{ barcode: "", barcodeType: "EAN-13", source: "Manual" }];
                  return (
                    <tr key={r.k} className={r.skip ? "skip" : r.ex ? "old" : "new"}>
                      <td className="c">
                        {r.ex ? (
                          <input
                            type="checkbox"
                            aria-label="Pick for delete"
                            checked={!!del[r.ex.variantID]}
                            onChange={(e) => setDel((m) => ({ ...m, [r.ex.variantID]: e.target.checked }))}
                          />
                        ) : null}
                      </td>
                      <td>{r.p.name}</td>
                      <td className="mono">{r.z.code}</td>
                      <td>
                        <span className="axsc-swatch" style={{ background: r.c.hexCode }} /> {r.c.name}
                      </td>
                      <td className="mono">{r.ex?.sku || <em style={{ color: "var(--ink-3)" }}>new</em>}</td>
                      <td>
                        {bcs.map((b, i) => (
                          <div className="axsc-bc" key={i}>
                            <input
                              type="text"
                              value={b.barcode}
                              disabled={r.skip}
                              placeholder={opts.autoBarcode ? "auto" : ""}
                              style={dupes.has(String(b.barcode).trim()) ? { borderColor: "var(--danger)" } : undefined}
                              aria-label={i ? "Additional barcode" : "Primary barcode"}
                              onChange={(e) => setBarcode(r, i, e.target.value)}
                            />
                            {i === 0 ? (
                              <button type="button" className="axa-iconbtn" title="Add another barcode" disabled={r.skip} onClick={() => addBarcode(r)}>
                                +
                              </button>
                            ) : (
                              <button type="button" className="axa-iconbtn" title="Remove this barcode" onClick={() => dropBarcode(r, i)}>
                                ×
                              </button>
                            )}
                          </div>
                        ))}
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          value={v.salesRate ?? ""}
                          disabled={r.skip}
                          placeholder={String(r.p.retailRate ?? r.p.salesRate ?? "")}
                          aria-label="Sales price"
                          onChange={(e) => edit(r.k, { salesRate: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          value={v.reorderLevel ?? ""}
                          disabled={r.skip}
                          placeholder={String(r.p.reorderLevel ?? "")}
                          aria-label="Re-order level"
                          onChange={(e) => edit(r.k, { reorderLevel: e.target.value })}
                        />
                      </td>
                      <td className="c">
                        <input type="checkbox" checked={!!v.isActive} disabled={r.skip} aria-label="Active" onChange={(e) => edit(r.k, { isActive: e.target.checked })} />
                      </td>
                    </tr>
                  );
                })}
                {opts.removeMissing &&
                  gone.map(({ k, v }) => (
                    <tr key={k} className="gone">
                      <td />
                      <td>{prodOf(v.productID)?.name || v.productName}</td>
                      <td className="mono">{v.sizeCode}</td>
                      <td>{v.colourName}</td>
                      <td className="mono">{v.sku}</td>
                      <td colSpan={4}>removed on save</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="axst-empty">Pick products, a size set, sizes and colours to see the variants.</div>
        )}
      </div>
    </div>
  );

  return (
    <AdminScreen
      title="Set Size & Colour"
      subtitle="Create variants (product + size + colour) with their own SKU, barcodes, price and re-order level"
      tools={
        <button type="button" className="axa-iconbtn" title="Reload" onClick={() => loadExisting(picked)}>
          <AxRefreshIcon />
        </button>
      }
      actions={
        <>
          {delIDs.length > 0 && (
            <button type="button" className="axa-btn axa-btn-danger" onClick={removeSelected}>
              Delete {delIDs.length}
            </button>
          )}
          <button
            type="button"
            className="axa-btn axa-btn-ghost"
            onClick={() => {
              setEdits({});
              setDel({});
            }}
            disabled={!Object.keys(edits).length}
          >
            Discard
          </button>
          <button type="button" className="axa-btn axa-btn-primary" onClick={save} disabled={busy || !rows.length}>
            {busy ? "Saving…" : "Save variants"}
          </button>
        </>
      }
      detail={editor}
      status={
        <AxStatusKeys
          keys={[
            { k: "+", t: "additional barcode" },
            { k: "Empty price", t: "the product's price applies" },
          ]}
          right={`${picked.length} products · ${rows.length} variants`}
        />
      }
    />
  );
};

export default SetSizeColour;
